import React, { useEffect, useMemo, useState } from 'react';
import {
  getCategories,
  getCategoryById,
  createCategory,
  updateCategory,
} from '../../services/productServices';
import { CrmButton, CrmDrawer, CrmInput, CrmSelect } from '../ui';
import NotificationSnackbar from '../ui/NotificationSnackbar';
import './catalog-drawers.scss';

const slugify = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

function AddCategoryDialog({ open, onClose, category = null }) {
  const [name, setName] = useState('');
  const [parentOptions, setParentOptions] = useState([]);
  const [parentId, setParentId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notif, setNotif] = useState({ open: false, message: '', severity: 'success' });

  const generatedSlug = useMemo(() => slugify(name), [name]);
  const showNotification = (message, severity = 'success') => setNotif({ open: true, message, severity });

  useEffect(() => {
    if (!open) return;
    let active = true;
    (async () => {
      try {
        const raw = await getCategories();
        const flat = [];
        const flatten = (list, path = []) => (list || []).forEach((cat) => {
          const fullPath = [...path, cat.name];
          flat.push({ id: cat.id, label: fullPath.join(' > ') });
          if (cat.children?.length) flatten(cat.children, fullPath);
        });
        flatten(raw);
        if (!active) return;
        setParentOptions(flat.filter((item) => Number(item.id) !== Number(category?.id)));

        if (category?.id) {
          try {
            const full = await getCategoryById(category.id);
            if (!active) return;
            setName(full.name || category.rawName || category.name || '');
            setParentId(full.parent_id || '');
          } catch {
            setName(category.rawName || category.name || '');
            setParentId(category.parent_id || '');
          }
        } else {
          setName('');
          setParentId('');
        }
      } catch {
        showNotification('Failed to fetch categories', 'error');
      }
    })();
    return () => { active = false; };
  }, [open, category]);

  const handleClose = () => {
    if (submitting) return;
    setName('');
    setParentId('');
    onClose();
  };

  const handleSave = async () => {
    if (submitting) return;
    if (!name.trim()) return showNotification('Category name is required', 'warning');
    setSubmitting(true);
    try {
      if (category?.id) {
        await updateCategory(category.id, { name: name.trim(), parent_id: parentId || null });
      } else {
        await createCategory({ category: name.trim(), parent_id: parentId || null });
      }
      showNotification(category?.id ? 'Category updated successfully' : 'Category created successfully');
      setSubmitting(false);
      setName('');
      setParentId('');
      onClose();
    } catch (error) {
      showNotification(error?.response?.data?.error || 'Failed to save category', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <CrmDrawer
        open={open}
        onClose={handleClose}
        title={category ? 'Edit Internal / ERP Category' : 'Add Internal / ERP Category'}
        subtitle="Internal categories are the single operational/ERP classification used by the core product record."
        size="md"
        footer={<CrmButton variant="primary" loading={submitting} onClick={handleSave}>Save Category</CrmButton>}
      >
        <div className="crm-form-stack">
          <CrmInput
            autoFocus
            required
            label="Category Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <CrmInput
            label="Slug"
            value={generatedSlug}
            readOnly
            helperText="Automatically generated from the category name."
          />
          <CrmSelect
            label="Parent Category"
            value={parentId || ''}
            onChange={(event) => setParentId(event.target.value)}
          >
            <option value="">No parent</option>
            {parentOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
          </CrmSelect>
        </div>
      </CrmDrawer>
      <NotificationSnackbar {...notif} onClose={() => setNotif((previous) => ({ ...previous, open: false }))} />
    </>
  );
}

export default AddCategoryDialog;
